// ============================================================================
// SERVICIO DE VALIDACIÓN DE RANGOS FISIOLÓGICOS
// Fase 4: Validación a prueba de errores
// ============================================================================

import { NCSTestResult } from '../types/ncs';
import { UnifiedEMGResults } from '../types/emg';
import { NORMAL_REFERENCE_VALUES } from '../data/nerveData';
import {
  PHYSIOLOGICAL_RANGES,
  AGE_STRATIFIED_REFERENCES,
  calculateSeverityLevel,
  getAgeCategory,
  type ValidationResult,
  type ValidationSeverity,
} from '../data/emgClinicalCriteria';

export { NORMAL_REFERENCE_VALUES };
export type { ValidationResult, ValidationSeverity };

type NumericRange = { min: number; max: number; unit: string };

export interface ValidationAlert {
  type: 'out_of_range' | 'suspicious' | 'impossible' | 'missing_data' | 'completeness';
  severity: 'error' | 'warning' | 'info';
  parameter: string;
  value?: number | string;
  expectedRange?: string;
  message: string;
  recommendation?: string;
}

export interface StudyCompletenessCheck {
  isComplete: boolean;
  missingStudies: string[];
  recommendations: string[];
  suspectedDiagnosis?: string;
}

/**
 * Servicio unificado de validación fisiológica (Sprint 3).
 * Fuente de rangos: emgClinicalCriteria.PHYSIOLOGICAL_RANGES
 */
export class PhysiologicalValidationService {

  // --- API detallada (ValidationResult) — usada por clinicalService, tests ---

  static validateRange(
    value: number,
    range: NumericRange,
    fieldName: string,
    context?: { patientAge?: number; testType?: string }
  ): ValidationResult {
    const isValid = value >= range.min && value <= range.max;

    if (isValid) {
      return {
        isValid: true,
        severity: 'info',
        message: `${fieldName} dentro del rango normal`,
        expectedRange: `${range.min}-${range.max} ${range.unit}`,
        actualValue: value,
        field: fieldName,
      };
    }

    const severityLevel = calculateSeverityLevel(value, range);
    const severity: ValidationSeverity =
      severityLevel === 'critical' ? 'critical' :
      severityLevel === 'severe' ? 'error' :
      severityLevel === 'moderate' ? 'warning' : 'info';

    const deviation = value < range.min ? 'por debajo' : 'por encima';
    const message = `${fieldName} ${deviation} del rango fisiológico normal (${value} ${range.unit})`;

    return {
      isValid: false,
      severity,
      message,
      expectedRange: `${range.min}-${range.max} ${range.unit}`,
      actualValue: value,
      field: fieldName,
      suggestions: this.generateSuggestions(fieldName, value, range, context),
    };
  }

  static validateNCSValues(
    results: { type?: 'motor' | 'sensory'; latency?: number; amplitude?: number; velocity?: number },
    patientAge?: number
  ): ValidationResult[] {
    const validationResults: ValidationResult[] = [];
    const ranges = PHYSIOLOGICAL_RANGES.ncs;
    const testType = results.type ?? 'motor';

    if (results.latency !== undefined) {
      const range = testType === 'motor' ? ranges.motor.latency : ranges.sensory.latency;
      validationResults.push(
        this.validateRange(results.latency, range, 'Latencia', { patientAge, testType })
      );
    }
    if (results.amplitude !== undefined) {
      const range = testType === 'motor' ? ranges.motor.amplitude : ranges.sensory.amplitude;
      validationResults.push(
        this.validateRange(results.amplitude, range, 'Amplitud', { patientAge, testType })
      );
    }
    if (results.velocity !== undefined) {
      const range = testType === 'motor' ? ranges.motor.velocity : ranges.sensory.velocity;
      validationResults.push(
        this.validateRange(results.velocity, range, 'Velocidad', { patientAge, testType })
      );
    }

    return validationResults;
  }

  static validateEMGValues(
    results: {
      motorUnitPotentials?: { duration?: number; amplitude?: number; polyphasia?: number };
    },
    patientAge?: number
  ): ValidationResult[] {
    const validationResults: ValidationResult[] = [];
    const ranges = PHYSIOLOGICAL_RANGES.emg.motorUnitPotentials;

    let adjustedRanges = ranges;
    if (patientAge) {
      const ageCategory = getAgeCategory(patientAge);
      const ageRefs = AGE_STRATIFIED_REFERENCES.motorUnitPotentials;
      adjustedRanges = {
        duration: {
          min: ranges.duration.min,
          max: ageRefs.duration[ageCategory].upperLimit,
          unit: ranges.duration.unit,
        },
        amplitude: {
          min: ageRefs.amplitude[ageCategory].lowerLimit,
          max: ranges.amplitude.max,
          unit: ranges.amplitude.unit,
        },
        polyphasia: ranges.polyphasia,
      };
    }

    const mup = results.motorUnitPotentials;
    if (mup?.duration !== undefined) {
      validationResults.push(
        this.validateRange(mup.duration, adjustedRanges.duration, 'Duración PUM', { patientAge })
      );
    }
    if (mup?.amplitude !== undefined) {
      validationResults.push(
        this.validateRange(mup.amplitude, adjustedRanges.amplitude, 'Amplitud PUM', { patientAge })
      );
    }
    if (mup?.polyphasia !== undefined) {
      validationResults.push(
        this.validateRange(mup.polyphasia, adjustedRanges.polyphasia, 'Polifasia PUM', { patientAge })
      );
    }

    return validationResults;
  }

  private static generateSuggestions(
    fieldName: string,
    value: number,
    range: NumericRange,
    context?: { patientAge?: number; testType?: string }
  ): string[] {
    const suggestions: string[] = [];
    const lower = fieldName.toLowerCase();

    if (lower.includes('latencia')) {
      if (value > range.max) {
        suggestions.push('Verificar la colocación de electrodos y distancia de estimulación');
        suggestions.push('Considerar neuropatía desmielinizante si el valor es muy alto');
        suggestions.push('Revisar temperatura de la extremidad (< 32°C puede prolongar latencias)');
      } else if (value < range.min) {
        suggestions.push('Verificar calibración del equipo');
        suggestions.push('Confirmar identificación correcta del potencial');
      }
    } else if (lower.includes('amplitud')) {
      if (value < range.min) {
        suggestions.push('Considerar pérdida axonal si la amplitud está muy reducida');
        suggestions.push('Verificar impedancia de electrodos');
        suggestions.push('Evaluar posible bloqueo de conducción');
      } else if (value > range.max) {
        suggestions.push('Verificar calibración del amplificador');
        suggestions.push('Revisar artefactos de estimulación');
      }
    } else if (lower.includes('velocidad')) {
      if (value < range.min) {
        suggestions.push('Considerar desmielinización si la velocidad está muy reducida');
        suggestions.push('Verificar temperatura de la extremidad');
        suggestions.push('Revisar distancia de medición entre puntos de estimulación');
      }
    }

    if (context?.patientAge && context.patientAge > 60) {
      suggestions.push('Considerar corrección por edad para pacientes > 60 años');
    }

    return suggestions;
  }

  private static ncsLimits(type: 'motor' | 'sensory') {
    return type === 'motor' ? PHYSIOLOGICAL_RANGES.ncs.motor : PHYSIOLOGICAL_RANGES.ncs.sensory;
  }

  private static resultToAlert(
    result: ValidationResult,
    parameter: string,
    alertType: ValidationAlert['type'] = 'impossible'
  ): ValidationAlert {
    return {
      type: alertType,
      severity: result.severity === 'critical' || result.severity === 'error' ? 'error' :
        result.severity === 'warning' ? 'warning' : 'info',
      parameter,
      value: result.actualValue,
      expectedRange: result.expectedRange,
      message: result.message,
      recommendation: result.suggestions?.[0],
    };
  }

  // --- API UI (ValidationAlert) — formularios y ResultAnalyzer ---

  /**
   * Validar un resultado NCS individual
   */
  static validateNCSResult(result: NCSTestResult): ValidationAlert[] {
    const alerts: ValidationAlert[] = [];
    const limits = this.ncsLimits(result.type === 'sensory' ? 'sensory' : 'motor');

    if (result.latency !== undefined && result.latency !== null) {
      const v = this.validateRange(result.latency, limits.latency, 'Latencia', { testType: result.type });
      if (!v.isValid) {
        alerts.push(this.resultToAlert(v, `${result.nerve} (${result.type}) - Latencia`));
      }
    }

    if (result.amplitude !== undefined && result.amplitude !== null) {
      const v = this.validateRange(result.amplitude, limits.amplitude, 'Amplitud', { testType: result.type });
      if (!v.isValid) {
        alerts.push(this.resultToAlert(v, `${result.nerve} (${result.type}) - Amplitud`));
      }
    }

    if (result.velocity !== undefined && result.velocity !== null && result.velocity > 0) {
      const v = this.validateRange(result.velocity, limits.velocity, 'Velocidad', { testType: result.type });
      if (!v.isValid) {
        alerts.push(this.resultToAlert(
          v,
          `${result.nerve} (${result.type}) - Velocidad`,
          v.actualValue < limits.velocity.min ? 'suspicious' : 'impossible'
        ));
      }
    }

    const tempRange = PHYSIOLOGICAL_RANGES.emg.temperature;
    if (result.temperature !== undefined) {
      if (result.temperature < tempRange.min || result.temperature > tempRange.max) {
        alerts.push({
          type: 'impossible',
          severity: 'error',
          parameter: 'Temperatura',
          value: result.temperature,
          expectedRange: `${tempRange.min}-${tempRange.max}${tempRange.unit}`,
          message: `Temperatura ${result.temperature}°C es fisiológicamente imposible`,
          recommendation: 'Verificar termómetro de superficie',
        });
      } else if (result.temperature < 32) {
        alerts.push({
          type: 'suspicious',
          severity: 'warning',
          parameter: 'Temperatura',
          value: result.temperature,
          expectedRange: '≥32°C (miembros superiores)',
          message: `Temperatura ${result.temperature}°C puede alterar latencias y velocidades`,
          recommendation: 'Calentar la extremidad antes de la medición. Las latencias se prolongan ~2ms/°C y las velocidades disminuyen ~2m/s/°C',
        });
      }
    }

    return alerts;
  }

  /**
   * Validar datos EMG de un músculo
   */
  static validateEMGData(emgData: UnifiedEMGResults): ValidationAlert[] {
    const alerts: ValidationAlert[] = [];

    const detailed = this.validateEMGValues({
      motorUnitPotentials: {
        duration: emgData.motorUnitActionPotentials.duration,
        amplitude: emgData.motorUnitActionPotentials.amplitude,
        polyphasia: emgData.motorUnitActionPotentials.polyphasia,
      },
    });

    for (const v of detailed) {
      if (!v.isValid) {
        alerts.push(this.resultToAlert(
          v,
          `${emgData.muscle} - ${v.field}`,
          v.severity === 'warning' ? 'suspicious' : 'impossible'
        ));
      }
    }

    // Validar coherencia entre recruitment e interference
    if (emgData.recruitment === 'reduced' && emgData.interferencePattern === 'full') {
      alerts.push({
        type: 'suspicious',
        severity: 'warning',
        parameter: `${emgData.muscle} - Reclutamiento/Interferencia`,
        message: 'Reclutamiento reducido con patrón de interferencia completo es contradictorio',
        recommendation: 'Revisar evaluación del reclutamiento vs patrón de interferencia'
      });
    }

    if (emgData.recruitment === 'early' && emgData.interferencePattern === 'discrete') {
      alerts.push({
        type: 'suspicious',
        severity: 'warning',
        parameter: `${emgData.muscle} - Reclutamiento/Interferencia`,
        message: 'Reclutamiento precoz con patrón discreto de interferencia es contradictorio',
        recommendation: 'El reclutamiento precoz debería generar un patrón lleno de interferencia'
      });
    }

    // Validar estabilidad + duración (MUAP inestable con duración normal = NMJ)
    const stability = emgData.motorUnitActionPotentials.stability;
    if (stability === 'unstable') {
      alerts.push({
        type: 'suspicious',
        severity: 'info',
        parameter: `${emgData.muscle} - Estabilidad MUAP`,
        message: 'MUAPs inestables (jiggle) detectados',
        recommendation: 'Considerar trastorno de unión neuromuscular (MG, LEMS, botulismo) o reinervación activa'
      });
    }

    return alerts;
  }

  /**
   * Verificar completitud del estudio según sospecha diagnóstica
   */
  static checkStudyCompleteness(
    ncsResults: NCSTestResult[],
    emgResults: UnifiedEMGResults[],
    suspectedDiagnosis?: string
  ): StudyCompletenessCheck {
    const missing: string[] = [];
    const recommendations: string[] = [];

    // Verificaciones generales
    const hasMotorNCS = ncsResults.some(r => r.type === 'motor');
    const hasSensoryNCS = ncsResults.some(r => r.type === 'sensory');
    const hasEMG = emgResults.length > 0;

    if (!hasMotorNCS) {
      missing.push('Estudios motores de conducción nerviosa');
    }
    if (!hasSensoryNCS) {
      missing.push('Estudios sensitivos de conducción nerviosa');
    }

    // Checklist por sospecha diagnóstica
    switch (suspectedDiagnosis) {
      case 'carpal_tunnel_syndrome':
        if (!ncsResults.some(r => r.nerve.toLowerCase().includes('median') && r.type === 'sensory')) {
          missing.push('Conducción sensitiva del nervio mediano (obligatorio para STC)');
        }
        if (!ncsResults.some(r => r.nerve.toLowerCase().includes('ulnar') && r.type === 'sensory')) {
          missing.push('Conducción sensitiva del nervio ulnar (comparación con mediano)');
          recommendations.push('Realizar comparación mediano-cubital para confirmar STC');
        }
        break;

      case 'polyneuropathy':
        if (!ncsResults.some(r => r.nerve.toLowerCase().includes('sural'))) {
          missing.push('Conducción del nervio sural (esencial para polineuropatía)');
        }
        if (!ncsResults.some(r => r.nerve.toLowerCase().includes('peroneal') || r.nerve.toLowerCase().includes('fibular'))) {
          missing.push('Conducción del nervio peroneo (esencial para polineuropatía)');
        }
        recommendations.push('Verificar patrón simétrico bilateral');
        recommendations.push('Evaluar gradiente longitud-dependiente');
        break;

      case 'motor_neuron_disease':
        // Criterios Awaji: 3 regiones anatómicas
        const bodyRegions = new Set(emgResults.map(e => this.getAnatomicRegion(e.muscle)));
        if (bodyRegions.size < 3) {
          missing.push(`EMG en al menos 3 regiones anatómicas (actual: ${bodyRegions.size})`);
          recommendations.push('Criterios Awaji requieren evidencia en ≥3 regiones (bulbar, cervical, torácica, lumbar)');
        }
        if (!hasSensoryNCS) {
          missing.push('Conducciones sensitivas (deben ser normales para confirmar ELA)');
        }
        break;

      case 'myasthenia_gravis':
        // Necesita RNS
        recommendations.push('Realizar estimulación repetitiva (RNS) a 2-3Hz en trapecio o facial');
        recommendations.push('Considerar SFEMG (jitter) si RNS es normal y alta sospecha clínica');
        break;
        
      case 'cidp':
        // Criterios EFNS/PNS
        if (ncsResults.length < 4) {
          missing.push('Evaluar al menos 4 nervios motores (criterios EFNS/PNS para CIDP)');
        }
        recommendations.push('Buscar prolongación de latencia distal >50%, reducción CV a <70% del normal');
        recommendations.push('Evaluar ondas F (latencia y persistencia) para neuropatía desmielinizante');
        break;
        
      case 'gbs':
        recommendations.push('Evaluar patrón "sural sparing" (sural normal con reducción de sensitivos en manos)');
        recommendations.push('Buscar ondas F ausentes o prolongadas');
        recommendations.push('Repetir estudio en 2-3 semanas si el estudio inicial es normal');
        break;
    }

    return {
      isComplete: missing.length === 0,
      missingStudies: missing,
      recommendations,
      suspectedDiagnosis
    };
  }

  /**
   * Validar todos los inputs de un estudio completo
   */
  static validateFullStudy(
    ncsResults: NCSTestResult[],
    emgResults: UnifiedEMGResults[]
  ): ValidationAlert[] {
    const allAlerts: ValidationAlert[] = [];

    // Validar cada resultado NCS
    ncsResults.forEach(result => {
      allAlerts.push(...this.validateNCSResult(result));
    });

    // Validar cada resultado EMG
    emgResults.forEach(result => {
      allAlerts.push(...this.validateEMGData(result));
    });

    // Validaciones cruzadas NCS-EMG
    allAlerts.push(...this.crossValidateNCSEMG(ncsResults, emgResults));

    return allAlerts;
  }

  /**
   * Validaciones cruzadas entre NCS y EMG
   */
  private static crossValidateNCSEMG(
    ncsResults: NCSTestResult[],
    emgResults: UnifiedEMGResults[]
  ): ValidationAlert[] {
    const alerts: ValidationAlert[] = [];

    // Si NCS motoras son normales pero EMG muestra denervación severa
    const normalMotorNCS = ncsResults.filter(r => r.type === 'motor' && r.status === 'normal');
    const severeEMG = emgResults.filter(r => 
      r.insertionalActivity === 'increased' && 
      r.recruitment === 'reduced'
    );

    if (normalMotorNCS.length > 0 && severeEMG.length > 0) {
      alerts.push({
        type: 'suspicious',
        severity: 'warning',
        parameter: 'NCS-EMG cross-validation',
        message: 'NCS motoras normales con denervación severa en EMG — sugiere lesión muy proximal o metodología inadecuada',
        recommendation: 'Evaluar raíces y plexo. Considerar ondas F y reflejos H.'
      });
    }

    // Si todo NCS anormal pero EMG normal
    const abnormalNCS = ncsResults.filter(r => r.status === 'abnormal');
    const normalEMG = emgResults.filter(r => r.status === 'normal');

    if (abnormalNCS.length > 2 && normalEMG.length === emgResults.length && emgResults.length > 0) {
      alerts.push({
        type: 'suspicious',
        severity: 'info',
        parameter: 'NCS-EMG cross-validation',
        message: 'NCS anormales con EMG completamente normal — posible neuropatía desmielinizante pura sin degeneración axonal',
        recommendation: 'Correlacionar con evolución temporal. La EMG puede ser normal en fases tempranas de desmielinización.'
      });
    }

    return alerts;
  }

  /**
   * Determinar región anatómica de un músculo (para criterios Awaji)
   */
  private static getAnatomicRegion(muscle: string): string {
    const muscleLower = muscle.toLowerCase();
    
    // Región bulbar
    if (['masseter', 'orbicularis_oris', 'genioglossus', 'tongue', 'mentalis', 'sternocleidomastoid'].some(m => muscleLower.includes(m))) {
      return 'bulbar';
    }
    // Región cervical (C5-T1)
    if (['deltoid', 'biceps', 'triceps', 'first_dorsal_interosseous', 'abductor_pollicis', 'extensor', 'flexor', 'pronator', 'supraspinatus', 'infraspinatus'].some(m => muscleLower.includes(m))) {
      return 'cervical';
    }
    // Región torácica
    if (['paraspinal', 'thoracic', 'rectus_abdominis', 'intercostal'].some(m => muscleLower.includes(m))) {
      return 'thoracic';
    }
    // Región lumbosacra (L2-S2)
    if (['tibialis', 'gastrocnemius', 'vastus', 'iliopsoas', 'soleus', 'peroneus', 'gluteus', 'abductor_hallucis', 'quadriceps'].some(m => muscleLower.includes(m))) {
      return 'lumbosacral';
    }
    
    return 'unknown';
  }
}
